import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, RotateCcw, Clock, HandCoins, Handshake, ArrowRight, PauseCircle } from "lucide-react";

const PROTECTIONS = [
  {
    icon: <Clock size={22}/>,
    title: "14-day cooling off",
    desc:
      "You may cancel your reservation at any time before signing the sale agreement and receive a full refund of your deposit within 14 working days. No paperwork, no questions.",
    testid: "protection-cooling-off",
  },
  {
    icon: <RotateCcw size={22}/>,
    title: "MAVULA buy-back option",
    desc:
      "At any time after the cooling-off period, MAVULA (or the project partner) may buy back your position at cost plus earned interest as set in your individual project contract — subject to project terms, liquidity and country law. Illustrative — this is not a promise of any specific rate or return.",
    testid: "protection-buy-back",
  },
  {
    icon: <PauseCircle size={22}/>,
    title: "Pause payments for life events",
    desc:
      "If life becomes difficult — job loss, illness, family crisis — you can pause payments for up to 12 months without penalty. Your position and rate are preserved.",
    testid: "protection-pause",
  },
  {
    icon: <Handshake size={22}/>,
    title: "Transfer to another MAVULA project",
    desc:
      "If your chosen project is significantly delayed or no longer suits you, you may transfer your equity into another live MAVULA project at cost — no cancellation fee.",
    testid: "protection-transfer",
  },
  {
    icon: <HandCoins size={22}/>,
    title: "Secondary marketplace",
    desc:
      "List your plot for sale to another investor inside the MAVULA marketplace. If a buyer is found, transfer is executed by independent lawyers.",
    testid: "protection-secondary",
  },
];

export default function ProtectionsPage() {
  return (
    <main data-testid="protections-page">
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Your protections</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98]">
            You are <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>never locked in.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed">
            Cooling off. Buy-back. Payment pauses. Project transfer. A right of exit is worth more than a slightly higher rate — so we've built every one you might need.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PROTECTIONS.map((p) => (
            <div key={p.title} className="mv-card p-6" data-testid={p.testid}>
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mv-green-text mb-3"
                   style={{ background: "rgba(212,175,55,0.16)" }}>{p.icon}</div>
              <div className="font-display text-xl mv-green-text">{p.title}</div>
              <p className="mt-2 text-[14px]" style={{ color: "var(--mv-muted)" }}>{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 mv-card p-6 flex flex-wrap items-center gap-4" style={{ background: "linear-gradient(180deg, #fff, rgba(212,175,55,0.10))" }}>
          <ShieldCheck size={24} className="mv-green-text"/>
          <div className="flex-1 min-w-[200px]">
            <div className="font-display text-lg mv-green-text">All protections are on your signed contract.</div>
            <p className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
              None of these are marketing promises. Each protection appears verbatim on your project contract, countersigned by independent lawyers.
            </p>
          </div>
          <Link to="/how-your-money-is-held" className="btn-green" data-testid="protections-cta-money">
            See how your money is held <ArrowRight size={15}/>
          </Link>
        </div>
      </section>
    </main>
  );
}
